import {
  Button,
  Checkbox,
  Message,
  Spinner,
  Separator
} from 'common/components';
import { LendingUserInformation } from 'common/modules/domain/lendingUser';
import React, { useEffect, useState } from 'react';
import { Glossary, BoardGL } from 'common/utils/glossary';
import { SubButtonType } from 'common/components/button';

interface CheckBoxState {
  /** 全選択にチェックしたか否か */
  allCheckState: boolean;
}

interface accountProps {
  lendingUser: LendingUserInformation | null;
  index: string;
  status: string;
  usage: number;
  limit: number;
  onClickCheckBox: () => void;
}

interface LendingUserBoardProps {
  accounts: accountProps[];
  isDisplay: boolean;
  isDisplaySub: boolean;
  isLendingUserProcessing: boolean;
  isLendingUserProcessingFail: boolean;
  quantity: number | null;
  purchaseId: string;
  purchaseName: string;
  language: string;
  isSelloutPlan: boolean;
  glossary: Glossary;
  canDelete: boolean;
  canCancel: boolean;
  checkedInit: string[];
  isDemo: boolean;
  isDeviceActivate: boolean;
  onClickClose: () => void;
  onClickRegister: () => void;
  onClickDelete: () => void;
  onClickCancel: () => void;
}

type useMultipleCheckedRet<T> = {
  checked: string[];
  toggleChecked: (account: accountProps) => void;
  allCheck: (accounts: any) => void;
  clearCheck: (accounts: any) => void;
};

export const LendingUserBoard: React.FC<LendingUserBoardProps> = ({
  accounts,
  isDisplay,
  isDisplaySub,
  isLendingUserProcessing,
  isLendingUserProcessingFail,
  purchaseName,
  language,
  isSelloutPlan,
  glossary,
  canDelete,
  canCancel,
  checkedInit,
  isDemo,
  isDeviceActivate,
  onClickClose,
  onClickRegister,
  onClickDelete,
  onClickCancel
}) => {
  let isAll = false;
  // checkedInit(チェック状態)が0ではない且つ利用状況リスト数と同じ場合はtrueとする
  if (checkedInit.length && checkedInit.length === accounts.length) {
    isAll = true;
  }
  const initCheckedState = {
    allCheckState: isAll
  };
  /** 全件選択のチェックボックスのチェック状態の定義と変更 */
  const [allChecked, setAllChecked] = useState<CheckBoxState>(initCheckedState);
  useEffect(() => {
    if (isDisplay) {
      setAllChecked(initCheckedState);
    }
  }, [isDisplay, accounts.length]);

  /**  利用状況のリストのチェックボックスの制御 */
  function useMultipleChecked<T>(
    canCheckItems: string[],
    initVal?: string[]
  ): useMultipleCheckedRet<T> {
    /** チェック済み管理用状態 */
    const [checked, setChecked] = useState<string[]>(initVal || []);
    useEffect(() => {
      if (isDisplay) {
        setChecked(initVal || []);
      }
    }, [isDisplay]);

    /** チェックボックスをクリックした時等のチェック済み、未チェックの入れ替え */
    const toggleChecked = (account: accountProps) => {
      let newChecked: string[] = [];
      if (checked.includes(account.index)) {
        newChecked = [...checked.filter((item) => item !== account.index)];
      } else {
        newChecked = [...checked.concat([account.index])];
      }
      setChecked(newChecked);
      account.onClickCheckBox();
      /** checkedを確認し、すべて選択している場合は全件選択のチェックボックスをtrueにする */
      if (newChecked.length === accounts.length) {
        setAllChecked({ allCheckState: true });
      } else {
        setAllChecked({ allCheckState: false });
      }
    };
    /** 全てチェック */
    const allCheck = (accounts: accountProps[]) => {
      accounts.map((account: accountProps) => {
        if (!checked.includes(account.index)) {
          account.onClickCheckBox();
        }
      });
      setChecked(canCheckItems);
    };
    /** 全てのチェックを解除 */
    const clearCheck = (accounts: accountProps[]) => {
      accounts.map((account: accountProps) => {
        if (checked.includes(account.index)) {
          account.onClickCheckBox();
        }
      });
      setChecked([]);
    };
    return {
      checked,
      toggleChecked,
      allCheck,
      clearCheck
    };
  }
  const {
    checked,
    toggleChecked,
    allCheck,
    clearCheck
  } = useMultipleChecked<string>(
    accounts.map((account) => account.index),
    checkedInit
  );

  /** deleteIndexのリストが空の場合、他ユーザ削除ボタンを無効化 */
  let deleteButtonType: SubButtonType = 'invalid';
  if (canDelete) {
    deleteButtonType = 'highPriority';
  } else {
    deleteButtonType = 'invalid';
  }
  /** 有償機能プランの場合、他ユーザー削除ボタンを無効化 */
  //if (isDeviceActivate) {
  //  deleteButtonType = 'invalid';
  //}

  /** cancelIndexのリストが空の場合、個別解約ボタンを無効化 */
  let cancelButtonType: SubButtonType = 'invalid';
  if (canCancel) {
    cancelButtonType = 'highPriority';
  } else {
    cancelButtonType = 'invalid';
  }

  /** 利用状況取得中の場合、新規設定ボタンを無効化 */
  let registerButtonType: SubButtonType = 'invalid';
  if (isLendingUserProcessing) {
    registerButtonType = 'invalid';
  } else {
    registerButtonType = 'highPriority';
  }

  const keyAndValue = (key: string, separator: string, value: string) => {
    return (
      <div className="textKeyAndValue">
        <div className="key">{key}</div>
        <div className="separator">{separator}</div>
        <div className="value">{value}</div>
      </div>
    );
  };

  /** 全選択のチェックボックス押下時の処理 */
  const onClickAllCheckBox = () => {
    if (!allChecked.allCheckState) {
      allCheck(accounts);
    } else {
      clearCheck(accounts);
    }
    setAllChecked({
      ...allChecked,
      allCheckState: !allChecked.allCheckState
    });
  };

  const countLendingUser = () => {
    let lendingUser = 0;
    accounts.map((account) => {
      if (
        (account.status == 'active' && account.usage > 0) ||
        (account.status == 'canceling' && account.usage > 0) ||
        account.status == 'loan' ||
        account.status == 'forced_return'
      ) {
        lendingUser += 1;
      }
    });
    return lendingUser;
  };

  const countLendingUserLimit = () => {
    let lendingUserLimit = 0;
    accounts.map((account) => {
      if (
        (account.status == 'active' && account.usage >= 0) ||
        (account.status == 'canceling' && account.usage >= 0) ||
        account.status == 'loan' ||
        account.status == 'forced_return' ||
        account.status == 'expired'
      ) {
        lendingUserLimit += 1;
      }
    });
    return lendingUserLimit;
  };

  const generateStatus = (account: accountProps) => {
    if (account.status == 'active') {
      if (account.usage == null || account.usage == 0) {
        return glossary.glx(BoardGL.unused);
      } else {
        return glossary.glx(BoardGL.duringUse);
      }
    } else if (
      account.status == 'canceling' ||
      account.status == 'forced_return'
    ) {
      return glossary.glx(BoardGL.cancelled);
    } else if (account.status == 'loan') {
      return glossary.glx(BoardGL.usageByOtherUsers);
    } else if (account.status == 'expired') {
      return glossary.glx(BoardGL.expired);
    } else {
      return '';
    }
  };

  const dispLoanTime = (createAt: string) => {
    return new Intl.DateTimeFormat(language, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(new Date(createAt));
  };

  const accountList = accounts.map((account, i) => {
    const translatedStatus = generateStatus(account);
    return window.innerWidth > 639 ||
      document.documentElement.clientWidth > 639 ? (
      <div key={account.index}>
        <label className="userListLabel">
          <div className={i === 0 ? 'userListFlexFirst' : 'userListFlex'}>
            <Checkbox
              id="id_CheckBox_Lending_User_Popup"
              value={'#' + account.index}
              onChange={() => toggleChecked(account)}
              checked={checked.includes(account.index)}
              data-test="lendingUerBoard-checkBox"
            />
            <div className="userListBlock">
              <div className="textInfoLeft">
                {'#' + account.index}
                {keyAndValue(
                  glossary.glx(BoardGL.status),
                  ':',
                  translatedStatus
                )}
                {account.usage == null && account.limit == null
                  ? null
                  : keyAndValue(
                      glossary.glx(BoardGL.deviceNumber),
                      ':',
                      '' + account.usage + '/' + account.limit
                    )}
              </div>
              <div className="textInfoRight">
                {(account.status == 'active' && account.usage > 0) ||
                (account.status == 'canceling' &&
                  account.usage > 0 &&
                  account.lendingUser == null)
                  ? glossary.glx(BoardGL.youUsing)
                  : account.lendingUser
                  ? account.lendingUser.email
                  : null}
                <br />
                {account.lendingUser
                  ? '(' + dispLoanTime(account.lendingUser.createAt) + ')'
                  : null}
              </div>
            </div>
          </div>
        </label>
      </div>
    ) : (
      <div key={account.index}>
        <label className="userListLabel">
          <div className={i === 0 ? 'userListFlexFirst' : 'userListFlex'}>
            <Checkbox
              id="id_CheckBox_Lending_User_Popup"
              value={'#' + account.index}
              onChange={() => toggleChecked(account)}
              checked={checked.includes(account.index)}
              data-test="lendingUerBoard-checkBox"
            />
            <div className="userListBlock">
              <div>{'#' + account.index}</div>
              <div>
                {glossary.glx(BoardGL.status) + ' : ' + translatedStatus}
              </div>
              <div>
                {(account.status == 'active' && account.usage > 0) ||
                (account.status == 'canceling' &&
                  account.usage > 0 &&
                  account.lendingUser == null)
                  ? glossary.glx(BoardGL.youUsing)
                  : account.lendingUser
                  ? account.lendingUser.email
                  : null}
              </div>
              <div>
                {account.lendingUser
                  ? '(' + dispLoanTime(account.lendingUser.createAt) + ')'
                  : null}
              </div>
              <div>
                {account.usage == null && account.limit == null
                  ? null
                  : glossary.glx(BoardGL.deviceNumber) +
                    ' : ' +
                    account.usage +
                    '/' +
                    account.limit}
              </div>
            </div>
          </div>
        </label>
      </div>
    );
  });

  const Footer = () => (
    <div className="footer">
      <div className="buttonFlex">
        <Button
          type="deleteButton"
          subType={deleteButtonType}
          onClick={onClickDelete}
          data-test="lendingUserBoard-deleteButton"
        >
          {glossary.glx(BoardGL.removeOtherUser)}
        </Button>
        <Button
          type="cancelButton"
          subType={cancelButtonType}
          onClick={onClickCancel}
          data-test="lendingUserBoard-cancelButton"
          style={{
            display: isSelloutPlan || isDemo ? 'none' : 'flex'
          }}
        >
          {glossary.glx(BoardGL.cancelPlan)}
        </Button>
      </div>
    </div>
  );

  const _component = (
    <div className="lendingUserBoard">
      <div className="header">
        <Button
          type="toolButton"
          subType="close"
          data-test="lendingUserBoard-toolButton"
          onClick={onClickClose}
        />
        <Message type="message" data-test="lendingUserBoard-message_1">
          {glossary.glx(BoardGL.managementAndCancellation)}
        </Message>
      </div>
      <div className="scrollArea">
        <Message
          type="message"
          subType="name"
          data-test="lendingUserBoard-message_2"
        >
          {purchaseName}
        </Message>
        <Message
          type="message"
          subType="left"
          data-test="registerLendingUser-message_left_1"
        >
          {glossary.glx(BoardGL.useSettingUserOther)}
        </Message>
        <div className="info">
          <Button
            type="registerButton"
            subType={registerButtonType}
            onClick={onClickRegister}
            data-test="lendingUserBoard-registerButton"
          >
            {` ` + glossary.glx(BoardGL.newPreference)}
          </Button>
        </div>
        <Separator
          type="separator"
          data-test="lendingUserBoardSeparator"
          className="dividingLine"
        />
        <div className="userList">
          {!isLendingUserProcessing &&
            !isLendingUserProcessingFail &&
            accounts.length > 0 && (
              <Message
                type="message"
                subType="left"
                data-test="registerLendingUser-message_left_2"
              >
                {isLendingUserProcessing
                  ? null
                  : glossary
                      .glx(BoardGL.usageSituation)
                      .replace('{0}', `${countLendingUser()}`)
                      .replace('{1}', `${countLendingUserLimit()}`)}
              </Message>
            )}
        </div>
        <div>
          <div className="allCheckFlex">
            <label
              className="allCheckLabel"
              style={{
                cursor: isLendingUserProcessing ? '' : 'pointer'
              }}
            >
              <Checkbox
                id="allCheckLendingUser"
                value="allCheckLendingUser"
                onChange={onClickAllCheckBox}
                checked={allChecked.allCheckState}
                disabled={isLendingUserProcessing}
                style={{
                  cursor: isLendingUserProcessing ? '' : 'pointer'
                }}
                data-test="lendingUerBoard-all-check-checkBox"
              />
              <div className="allCheckLabelArea">
                {glossary.glx(BoardGL.allCheck)}
              </div>
            </label>
          </div>
        </div>
        <div>
          {isLendingUserProcessingFail && (
            <Message
              type="message"
              className="errorMessage"
              data-test="lendingUserBoard-message_error"
            >
              {glossary.glx(BoardGL.couldNotGet)}
            </Message>
          )}
          {accountList}
          {isLendingUserProcessing ? <Spinner /> : null}
        </div>
      </div>
      {Footer()}
    </div>
  );
  return isDisplay || isDisplaySub ? _component : null;
};
